import * as THREE from 'three';
import { useMemo } from 'react';
import CubicSpaceAvailableLabel from './view/CubicSpaceAvailableLabel';
import ProductClearanceLabel from './view/ProductClearanceLabel';
import { useNavigation } from '../function/navigation';
import { UnitConversion } from '../inputs/UnitConversion';

function Product({
  result,
  convRatio,
  product,
  t,
  drawingBaseDimensions,
  color = 'DarkOrange',
  fixedPosition = null,
  clearance = false,
  unitSystem = 'metric',
}) {
  const { nav } = useNavigation();
  const h2 = drawingBaseDimensions.h2;
  if (!product || !h2) return null;

  const rectShape = product.product_shape !== 'round';
  const productLength =
    (rectShape ? product.length : product.diameter) * convRatio;
  const productWidth =
    (rectShape ? product.width : product.diameter) * convRatio;
  const productHeight = product.height * convRatio;

  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;

  const turns = drawingBaseDimensions.nbr_windings * reverseConfig;

  const inclinationLe =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_le) *
    reverseConfig;
  const inclinationLe_db = inclinationLe / 2;
  const heightDiffLE =
    (Math.tan(inclinationLe) *
      reverseConfig *
      drawingBaseDimensions.le_angular +
      Math.tan(inclinationLe_db) *
        drawingBaseDimensions.le_double_bend_value *
        reverseConfig) *
    convRatio;

  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_center) *
    reverseConfig;
  const inclination_db = inclination / 2;
  const heightDiffUE =
    (Math.tan(inclination) * reverseConfig * drawingBaseDimensions.ue_angular +
      Math.tan(inclination_db) *
        drawingBaseDimensions.ue_double_bend_value *
        reverseConfig) *
    convRatio;

  const slopeAngleCenterRad = THREE.MathUtils.degToRad(
    drawingBaseDimensions.slope_angle_center
  );

  const height = convRatio * h2 - heightDiffLE - heightDiffUE;
  const outerRadius =
    convRatio * drawingBaseDimensions.column_centre_to_inside_outer_guide;
  const radius =
    (outerRadius ** 2 - (productLength / 2) ** 2) ** 0.5 - productWidth / 2;

  const angle1 = Math.asin(productLength / 2 / outerRadius);

  const innerRadius =
    drawingBaseDimensions.column_centre_to_inside_windings * convRatio;
  const innerProduct =
    (outerRadius ** 2 - (productLength / 2) ** 2) ** 0.5 - productWidth;

  const angle2 = Math.atan(productLength / 2 / innerProduct);

  const xLim =
    innerProduct < innerRadius
      ? (innerRadius ** 2 - innerProduct ** 2) ** 0.5 / productLength
      : 0;

  const xProdEdge =
    (innerProduct / productLength) *
    (productLength / (innerProduct * (angle1 + angle2)) - 1) ** 0.5;

  const heightLim =
    Math.sin(slopeAngleCenterRad) *
    (angle1 + Math.atan((xLim * productLength) / innerProduct)) *
    ((drawingBaseDimensions.central_diam / 2) * convRatio);

  const heightProdEdge =
    Math.sin(slopeAngleCenterRad) *
    (angle1 + Math.atan((xProdEdge * productLength) / innerProduct)) *
    ((drawingBaseDimensions.central_diam / 2) * convRatio);

  let maxX;
  let heightMaxX;
  if (heightLim / (0.5 + xLim) > heightProdEdge / (0.5 + xProdEdge)) {
    maxX = xLim;
    heightMaxX = heightLim;
  } else if (xProdEdge < 0.5) {
    maxX = xProdEdge;
    heightMaxX = heightProdEdge;
  } else {
    maxX = 0.5;
    heightMaxX =
      Math.sin(slopeAngleCenterRad) *
      (angle1 + angle2) *
      ((drawingBaseDimensions.central_diam / 2) * convRatio);
  }

  const inclineAngle =
    Math.asin(heightMaxX / ((0.5 + maxX) * productLength)) * reverseConfig;

  const prodHeightDiff = heightMaxX / (0.5 + maxX);

  const gapCenter =
    prodHeightDiff / 2 -
    Math.sin(slopeAngleCenterRad) *
      angle1 *
      ((drawingBaseDimensions.central_diam / 2) * convRatio);

  const angle = t * turns * Math.PI * 2;
  const x = radius * Math.cos(angle);
  const z = radius * Math.sin(angle);
  const y =
    t * height +
    heightDiffLE +
    (drawingBaseDimensions.h1 + 5) * convRatio +
    productHeight / 2 +
    gapCenter;

  const horClearance =
    result?.windings_horizontal_clearance?.[nav.focusedProduct] ?? 0;
  const clearanceWidth = Math.abs(horClearance) * convRatio;
  const clearanceRadius =
    radius - productWidth / 2 - (horClearance / 2) * convRatio;
  const posHorClearance = [
    clearanceRadius * Math.cos(angle),
    y,
    clearanceRadius * Math.sin(angle),
  ];

  const { displayedValue: displayedHorClearance } = UnitConversion(
    horClearance,
    unitSystem,
    'mm'
  );

  const verticalClearance =
    nav.focusedProduct === 0
      ? result.vertical_clearance_without_product
      : result.motor_clearance?.length > 0
        ? Math.min(
            result.ue_vertical_clearance?.[nav.focusedProduct] ?? 0,
            result.motor_clearance?.[nav.focusedProduct] ?? 0
          )
        : (result.ue_vertical_clearance?.[nav.focusedProduct] ?? 0);
  const clearanceHeight = Math.abs(verticalClearance) * convRatio;

  const { displayedValue: displayedVerClearance } = UnitConversion(
    verticalClearance,
    unitSystem,
    'mm'
  );

  const posVerClearance = [
    radius * Math.cos(angle) +
      Math.sin(inclineAngle) *
        (productHeight / 2 + (verticalClearance / 2) * convRatio) *
        Math.sin(angle),
    y +
      Math.cos(inclineAngle) *
        (productHeight / 2 + (verticalClearance / 2) * convRatio),
    radius * Math.sin(angle) -
      Math.sin(inclineAngle) *
        (productHeight / 2 + (verticalClearance / 2) * convRatio) *
        Math.cos(angle),
  ];

  const isCubicSpace = color === 'CubicSpaceAvailable';
  const colorToShow = isCubicSpace ? 'green' : color;

  const position = fixedPosition ? [0, 0, 0] : [x, y, z];
  const rotation = fixedPosition ? [0, 0, 0] : [-inclineAngle, -angle, 0];

  const textSize = 20 / drawingBaseDimensions.nbr_windings;

  const labelOffset = reverseConfig === 1 ? textSize / 25 : 0;

  const { displayedValue: displayedWidth } = UnitConversion(
    productWidth / convRatio,
    unitSystem,
    'mm'
  );

  const { displayedValue: displayedLength } = UnitConversion(
    productLength / convRatio,
    unitSystem,
    'mm'
  );

  const { displayedValue: displayedHeight } = UnitConversion(
    productHeight / convRatio,
    unitSystem,
    'mm'
  );

  return (
    <group>
      <mesh rotation-order="YXZ" position={position} rotation={rotation}>
        {rectShape ? (
          <boxGeometry args={[productWidth, productHeight, productLength]} />
        ) : (
          <RoundProduct
            productWidth={productWidth}
            productHeight={productHeight}
          />
        )}
        <meshStandardMaterial
          color={colorToShow}
          transparent={isCubicSpace}
          opacity={isCubicSpace ? 0.5 : 1}
        />
        {isCubicSpace && (
          <CubicSpaceAvailableLabel
            productLength={productLength}
            productWidth={productWidth}
            productHeight={productHeight}
          />
        )}
      </mesh>
      {isCubicSpace && (
        <>
          <group
            position={[
              position[0] + (productWidth / 2 + labelOffset) * Math.cos(angle),
              position[1],
              position[2] + (productWidth / 2 + labelOffset) * Math.sin(angle),
            ]}
          >
            <ProductClearanceLabel
              value={displayedWidth}
              reverseConfig={-1}
              textSize={textSize}
              color="green"
            />
          </group>
          <group
            position={[
              position[0] + labelOffset * Math.cos(angle),
              position[1] + productHeight / 4,
              position[2] + labelOffset * Math.sin(angle),
            ]}
          >
            <ProductClearanceLabel
              value={displayedHeight}
              reverseConfig={-1}
              textSize={textSize}
              color="red"
            />
          </group>
          <group
            position={[
              position[0] -
                (productWidth / 2) *
                  Math.sin(Math.PI * 2 - angle) *
                  reverseConfig +
                labelOffset * Math.cos(angle),
              position[1] - productHeight / 4,
              position[2] -
                (productWidth / 2) *
                  Math.cos(Math.PI * 2 - angle) *
                  reverseConfig +
                labelOffset * Math.sin(angle),
            ]}
          >
            <ProductClearanceLabel
              value={displayedLength}
              reverseConfig={-1}
              textSize={textSize}
              color="blue"
            />
          </group>
        </>
      )}

      {clearance && (
        <>
          <mesh
            rotation-order="YXZ"
            position={posHorClearance}
            rotation={rotation}
          >
            <boxGeometry
              args={[clearanceWidth, productHeight, productLength * 1.05]}
            />
            <meshBasicMaterial
              color={horClearance > 0 ? 'green' : 'red'}
              transparent={true}
              opacity={0.35}
              side={THREE.DoubleSide}
            />
          </mesh>
          <group position={posHorClearance}>
            <ProductClearanceLabel
              value={displayedHorClearance}
              reverseConfig={-1}
              textSize={textSize}
            />
          </group>
          <mesh
            rotation-order="YXZ"
            position={posVerClearance}
            rotation={rotation}
          >
            <boxGeometry
              args={[productWidth, clearanceHeight, productLength * 1.05]}
            />
            <meshBasicMaterial
              color={verticalClearance > 0 ? 'green' : 'red'}
              transparent={true}
              opacity={0.35}
              side={THREE.DoubleSide}
            />
          </mesh>
          <group position={posVerClearance}>
            <ProductClearanceLabel
              value={displayedVerClearance}
              reverseConfig={-1}
              textSize={textSize}
            />
          </group>
        </>
      )}
    </group>
  );
}

function RoundProduct({ productWidth, productHeight }) {
  const geometry = useMemo(() => {
    return new THREE.CylinderGeometry(
      productWidth / 2,
      productWidth / 2,
      productHeight,
      32,
      1,
      false
    );
  }, [productWidth, productHeight]);

  return <bufferGeometry attach="geometry" {...geometry} />;
}

export default Product;
